/**
 * RP-3 Schedule home — shared helpers for every generation entry point (Schedule home, Funding ▸ Recurring Services).
 * ONE scheduler: every surface posts to the canonical E1 route. Pure helpers are Jest-testable; `runGenerate` wraps the call.
 */
export const GENERATION_ERROR_HINTS = {
  AGREEMENT_NOT_ACTIVE: 'Only an ACTIVE service agreement authorises shift generation (§8) — activate or renew the agreement first',
  PSI_NOT_LINKED: 'This schedule is not linked to an agreement line and cannot generate shifts (§8)',
  AGREEMENT_LINE_REQUIRED: 'Create the schedule from a line of an ACTIVE service agreement (§8)',
  RULE_ARCHIVED: 'Archived schedules do not generate new shifts',
  SIL_HOUSE_REQUIRED: 'The participant needs an active SIL house placement for this schedule',
  RECURRENCE_INDEX_UNAVAILABLE: 'Generation is temporarily unavailable (idempotency index not ready) — try again shortly',
  RANGE_TOO_LARGE: 'Generate at most 366 days at a time',
};

/** Row state for one participant schedule (PSI): linked / unlinked / archived + the Generate disabled reason. */
export const scheduleRowState = (si) => {
  if (!si) return { linked: false, disabled: true, reason: '' };
  if (si.archived_at) return { linked: !!si.agreement_line_id, archived: true, disabled: true, reason: GENERATION_ERROR_HINTS.RULE_ARCHIVED };
  if (!si.agreement_line_id) return { linked: false, disabled: true, reason: GENERATION_ERROR_HINTS.PSI_NOT_LINKED, badge: 'Not linked to an agreement line' };
  return { linked: true, disabled: false, reason: '' };
};

/** Toast text for a generation result (created / existing / outside agreement). */
export const generationSummary = (d = {}) => {
  const created = d.created_count ?? 0;
  const existed = d.already_existing_count ?? 0;
  const outside = d.outside_agreement_count ?? 0;
  let msg;
  if (created > 0) {
    msg = `Created ${created} shift(s)`;
    if (existed) msg += ` · ${existed} already existed`;
  } else if (existed > 0) {
    msg = `No new shifts — ${existed} already exist for this period`;
  } else {
    msg = 'No shifts generated for this period';
  }
  if (outside > 0) msg += ` · ${outside} outside the agreement period (not created)`;
  return msg;
};

/** Lines of an agreement that can be scheduled (have a catalogue item) — used by the line-aware create dialog. */
export const schedulableLines = (agreements = []) => agreements
  .filter((a) => (a.effective_status || a.status) === 'ACTIVE')
  .flatMap((a) => (a.line_items || []).filter((li) => li.service_catalog_id).map((li) => ({
    key: `${a.id}:${li.agreement_line_id}`, agreement_id: a.id, agreement_no: a.agreement_no, agreement_line_id: li.agreement_line_id,
    service_catalog_id: li.service_catalog_id, label: `${a.agreement_no || 'Agreement'} · ${li.service_name || li.support_item_number || 'line'}`,
  })));

/** Runs E1 for one schedule; emits warnings first so the success toast (with "View in Calendar") stays on top. */
export const runGenerate = async ({ api, toast, navigate, notify }, id, body, onDone) => {
  try {
    const res = await api.post(`/services/participant-items/${id}/generate-shifts`, body || {});
    const d = res.data || {};
    (d.warnings || []).forEach((w) => toast.warning(w.message));
    const jumpDate = (d.effective_period || {}).from_date;
    toast.success(generationSummary(d), jumpDate ? { duration: 8000, action: { label: 'View in Calendar', onClick: () => navigate(`/roster/scheduling-board?date=${jumpDate}`) } } : undefined);
    onDone?.(d);
    return d;
  } catch (e) {
    const code = e?.response?.data?.detail?.code || e?.response?.data?.error?.code;
    if (code && GENERATION_ERROR_HINTS[code]) toast.error(GENERATION_ERROR_HINTS[code]);
    else notify.error(e, 'SAVE_FAILED');
    return null;
  }
};
